import 'katex/dist/katex.min.css';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { createApiUrl } from '../../../config/api';

interface PDFViewerProps {
  pdfFileName: string;
  markdownContent: string;
  sessionID: string;
  userID: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfFileName,
  markdownContent,
  sessionID,
  userID,
}) => {
  const pdfUrl = createApiUrl(
    `/file_handling/users/${userID}/sessions/${sessionID}/files/uploaded/file_types/pdf/${encodeURIComponent(pdfFileName)}`
  );

  // Replace escaped newlines with actual newlines
  const processedMarkdown = markdownContent.replace(/\\n/g, '\n');

  return (
    <div className="flex h-full w-full gap-4 p-4">
      {/* Left side: PDF viewer */}
      <div className="flex-1 border rounded-lg overflow-hidden bg-gray-50">
        <div className="bg-gray-100 px-4 py-2 border-b">
          <h3 className="text-sm font-semibold text-gray-700">Original PDF</h3>
        </div>
        <div className="h-full overflow-auto p-2">
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-full h-full min-h-[600px]"
            style={{ minHeight: 'calc(100vh - 200px)' }}
          >
            <p className="p-4 text-gray-600">
              Unable to display PDF. <a href={pdfUrl} className="text-blue-600 underline" download>Download PDF</a>
            </p>
          </object>
        </div>
      </div>

      {/* Right side: Markdown viewer */}
      <div className="flex-1 border rounded-lg overflow-hidden bg-white">
        <div className="bg-gray-100 px-4 py-2 border-b">
          <h3 className="text-sm font-semibold text-gray-700">Transcription</h3>
        </div>
        <div className="h-full overflow-auto p-6 prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              table: ({ node, ...props }) => (
                <table className="border-collapse border border-gray-300 w-full my-4" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-semibold" {...props} />
              ),
              td: ({ node, ...props }) => (
                <td className="border border-gray-300 px-4 py-2" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="list-decimal list-outside ml-6 my-4 space-y-2" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="list-disc list-outside ml-6 my-4 space-y-2" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="pl-2" {...props} />
              ),
              p: ({ node, ...props }) => (
                <p className="my-2 leading-relaxed" {...props} />
              ),
              h1: ({ node, ...props }) => (
                <h1 className="text-2xl font-bold my-4" {...props} />
              ),
              h2: ({ node, ...props }) => (
                <h2 className="text-xl font-bold my-3" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-lg font-semibold my-2" {...props} />
              ),
              code: ({ node, className, children, ...props }) => {
                const inline = !className;
                return inline ? (
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props}>
                    {children}
                  </code>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {processedMarkdown}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
